import React from "react";

/**
 * Barrière d'erreur de l'aperçu de page.
 *
 * Un bloc qui lève pendant le rendu emportait TOUT l'aperçu : React démonte l'arbre entier et
 * l'iframe devient blanche, sans le moindre message. C'est arrivé en vrai le 21/08 avec un
 * `PageHeader` portant `breadcrumb: "auto"`, un marqueur résolu au déploiement côté serveur :
 * l'aperçu, lui, rend le contenu SAISI, donc le marqueur, et le rendu levait.
 *
 * Le composant fautif est remplacé par un encart qui NOMME le bloc et donne le message d'erreur,
 * le reste de la page continue de s'afficher. Le rendu public n'est pas concerné : l'artefact
 * déployé porte des valeurs déjà résolues.
 */
export type PreviewErrorBoundaryProps = {
	children: React.ReactNode;
	/** Type du bloc, quand l'appelant le connaît : il est affiché tel quel dans l'encart. */
	label?: string;
	/** Averti une seule fois par erreur : l'aperçu peut alors basculer bloc par bloc. */
	onError?: (message: string) => void;
};

type State = { message: string | null; source: string | null };

/**
 * Le premier composant REACT de la pile d'erreur, celui qui a levé. Les éléments de balisage
 * (`div`, `span`...) sont sautés : ils ne désignent aucun bloc. Fonction pure, testée seule.
 */
export const blocNomDepuisPile = (pile?: string | null): string | null => {
	if (!pile) return null;
	for (const ligne of pile.split("\n")) {
		const nom = ligne.trim().replace(/^at\s+/, "").split(" ")[0]?.trim();
		if (!nom) continue;
		// Un composant React porte une majuscule ; une balise HTML est en minuscules.
		if (/^[A-Z][A-Za-z0-9_]*$/.test(nom)) return nom;
	}
	return null;
};

class PreviewErrorBoundary extends React.Component<PreviewErrorBoundaryProps, State> {
	state: State = { message: null, source: null };

	static getDerivedStateFromError(error: unknown): Partial<State> {
		const message = error instanceof Error ? error.message : String(error);
		return { message: message || "erreur inconnue" };
	}

	componentDidCatch(error: unknown, info: React.ErrorInfo) {
		this.setState({ source: blocNomDepuisPile(info?.componentStack) });
		const message = error instanceof Error ? error.message : String(error);
		this.props.onError?.(message);
	}

	private reessayer = () => this.setState({ message: null, source: null });

	render() {
		if (this.state.message === null) return this.props.children;

		const nom = this.props.label || this.state.source;
		return (
			<div className="my-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
				<p className="font-semibold">
					{nom ? `Le bloc ${nom} n'a pas pu être affiché` : "Un bloc n'a pas pu être affiché"}
				</p>
				<p className="mt-1 text-xs">
					Le reste de la page reste visible. Cet écart ne concerne que l'aperçu : le contenu publié est
					fabriqué par le serveur, avec ses valeurs déjà résolues.
				</p>
				<p className="mt-1 font-mono text-[11px] text-amber-800">{this.state.message}</p>
				<button
					type="button"
					onClick={this.reessayer}
					className="mt-2 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
				>
					Réessayer
				</button>
			</div>
		);
	}
}

export default PreviewErrorBoundary;
